import { Building2, Compass, Target, TrendingUp } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { SectionHead } from "@/components/SectionHead";
import { CtaStrip } from "@/components/CtaStrip";

const BELIEFS = [
  "Factual, neutral, methodology-first",
  "Data over opinion",
  "Transparency over claims",
  "Evidence before assertion",
];

const About = () => (
  <div data-testid="about-page">
    <Seo
      title="About Anlytx for Elections — Built by SevenM Technologies | Anlytx for Elections"
      description="Anlytx for Elections is a data-centric election intelligence platform built by SevenM Technologies, an IT consulting and digital transformation company established in 2008."
    />

    <section className="relative overflow-hidden bg-[#0b1322]" data-testid="about-hero">
      <div className="absolute inset-0 grid-faint-dark opacity-70" />
      <div className="absolute -top-32 left-1/2 h-80 w-[70%] -translate-x-1/2 rounded-full bg-[#b91c1c]/15 blur-3xl" />
      <div className="relative mx-auto max-w-4xl px-6 py-20 text-center sm:py-28">
        <Reveal>
          <p className="font-mono-data text-xs font-medium uppercase tracking-[0.24em] text-red-400">About</p>
          <h1 className="mt-4 font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl text-balance">
            About Anlytx for Elections
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-slate-300 md:text-lg">
            A data-centric election intelligence platform built to help India understand its elections through
            structured data, interactive exploration and transparent analysis.
          </p>
        </Reveal>
      </div>
    </section>

    <section className="bg-white" data-testid="about-mission">
      <div className="mx-auto max-w-7xl px-6 py-20 sm:py-24">
        <div className="grid gap-6 md:grid-cols-2">
          <Reveal className="h-full">
            <div className="h-full rounded-xl border border-slate-200 bg-[#f8fafc] p-8" data-testid="about-mission-card">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-[#1a2332]">
                <Target className="h-5 w-5 text-white" />
              </span>
              <h2 className="mt-6 font-heading text-2xl font-bold tracking-tight text-slate-900">Our Mission</h2>
              <p className="mt-3 text-base leading-relaxed text-slate-600">
                Make Indian election information structured, searchable, comparable and analytically useful — for
                everyone from newsrooms to researchers to the general public.
              </p>
              <p className="mt-3 text-base leading-relaxed text-slate-600">
                Election data in India is vast, but it is often scattered across reports, PDFs and constituency-level
                tables that are hard to search or compare. We bring it together in one place, so a voter, journalist
                or analyst can go from a question to an answer in moments — not days.
              </p>
              <p className="mt-3 text-base leading-relaxed text-slate-600">
                We believe transparent, easy-to-use data strengthens public understanding and informed discussion
                around elections.
              </p>
              <div className="mt-6 grid grid-cols-2 gap-3">
                {[
                  ["Structured", "Clean, consistent data"],
                  ["Searchable", "Find anything fast"],
                  ["Comparable", "Across years & seats"],
                  ["Insightful", "Analytically useful"],
                ].map(([t, d]) => (
                  <div key={t} className="rounded-lg border border-slate-200 bg-white px-4 py-3">
                    <p className="font-heading text-sm font-bold uppercase tracking-wide text-slate-900">{t}</p>
                    <p className="mt-0.5 font-mono-data text-[10px] uppercase tracking-[0.14em] text-slate-500">{d}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12} className="h-full">
            <div className="h-full rounded-xl border border-slate-200 bg-[#f8fafc] p-8" data-testid="about-background-card">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-[#1a2332]">
                <Building2 className="h-5 w-5 text-white" />
              </span>
              <h2 className="mt-6 font-heading text-2xl font-bold tracking-tight text-slate-900">Our Background</h2>
              <p className="mt-3 text-base leading-relaxed text-slate-600">
                Anlytx for Elections is built by SevenM Technologies — an IT consulting and digital transformation
                company founded in 2008 and restructured as a private limited company in 2013, with offices in
                Kolkata, Bengaluru and Dubai. The company has served 25,000+ clients and delivered 6,000+ solutions
                across 15+ service verticals.
              </p>
              <p className="mt-3 text-base leading-relaxed text-slate-600">
                In 2017, SevenM was selected to provide web-casting services to the Election Commission of India in
                Jharkhand — the team&apos;s first hands-on election-technology assignment, completed on time and to
                the client&apos;s expectations.
              </p>
              <div className="mt-6 grid grid-cols-2 gap-3">
                {[
                  ["2008", "Founded"],
                  ["25,000+", "Clients Served"],
                  ["6,000+", "Solutions Delivered"],
                  ["2017", "ECI Web-Casting Project"],
                ].map(([v, l]) => (
                  <div key={l} className="rounded-lg border border-slate-200 bg-white px-4 py-3">
                    <p className="font-mono-data text-lg font-semibold tracking-tight text-[#1a2332]">{v}</p>
                    <p className="text-[11px] font-medium uppercase tracking-wider text-slate-500">{l}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    <section className="relative overflow-hidden bg-[#f8fafc]" data-testid="about-beliefs">
      <div className="absolute inset-0 dot-grid opacity-30" />
      <div className="relative mx-auto max-w-7xl px-6 py-20 sm:py-24">
        <SectionHead
          eyebrow="What We Believe"
          title="Principles That Don't Bend During Election Season"
        />
        <div className="mx-auto mt-12 grid max-w-4xl gap-4 sm:grid-cols-2">
          {BELIEFS.map((b, i) => (
            <Reveal key={b} delay={(i % 2) * 0.08}>
              <div
                className="flex items-center gap-4 rounded-lg border border-slate-200 bg-white p-6"
                data-testid={`belief-${i + 1}`}
              >
                <span className="font-mono-data text-sm font-semibold text-[#b91c1c]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="font-heading text-base font-semibold text-slate-900">{b}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-white" data-testid="about-direction">
      <div className="mx-auto max-w-4xl px-6 py-20 text-center sm:py-24">
        <Reveal>
          <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-md bg-[#fef2f2]">
            <Compass className="h-5 w-5 text-[#b91c1c]" />
          </span>
          <h2 className="mt-6 font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
            Where We&apos;re Going
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">
            From an election-results dashboard to a broader election-data, political-analysis and media-infrastructure
            platform — without changing the fundamental commitment to accuracy and neutrality.
          </p>
          <div
            className="mx-auto mt-10 flex max-w-3xl flex-wrap items-center justify-center gap-3"
            data-testid="direction-path"
          >
            <TrendingUp className="h-5 w-5 text-[#b91c1c]" />
            {["Results Dashboard", "Data Platform", "Media Infrastructure"].map((s, i) => (
              <span key={s} className="flex items-center gap-3">
                {i > 0 && <span className="font-heading text-lg font-bold text-[#b91c1c]">&rarr;</span>}
                <span
                  className={`rounded-md border px-4 py-2.5 font-heading text-sm font-semibold ${
                    i === 2
                      ? "border-[#b91c1c] bg-[#b91c1c] text-white"
                      : "border-slate-300 bg-white text-slate-800"
                  }`}
                >
                  {s}
                </span>
              </span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>

    <CtaStrip
      testid="about-cta-strip"
      heading="Work With a Team That Treats Election Data Seriously"
      subheading="Whether you're a newsroom, a researcher or a data platform — we'd like to hear what you're building."
      primary={{ label: "Get in Touch", to: "/contact" }}
      secondary={{ label: "Read Our Methodology", to: "/methodology" }}
    />
  </div>
);

export default About;
